import React, { useCallback, useEffect, useRef, useState } from 'react';
import { getStroke } from 'perfect-freehand';
import { SIGNATURE_INK_COLORS } from '../constants/signing';
import { trimCanvasToInk, getInkBoundingBox } from '../utils/signatureProcessing';
import { useDialogA11y } from '../utils/useDialogA11y';

interface SignaturePadModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (dataUrl: string) => void;
  title?: string;
  /** When false, Escape/focus-trap are disabled (e.g. while a choice dialog is on top). */
  trapFocus?: boolean;
  /** Keep mounted but visually hidden — preserves in-progress strokes. */
  hidden?: boolean;
}

interface Stroke {
  /** Points as [x, y, pressure] in CSS pixels relative to the pad. */
  points: number[][];
  /** True for mouse/touch (derive width from velocity); false for real pen pressure. */
  simulatePressure: boolean;
}

const PAD_HEIGHT_PX = 180;
const GUIDELINE_OFFSET_PX = 38;
const EXPORT_TRIM_MARGIN_CSS_PX = 10;

const STROKE_OPTIONS = {
  size: 5,
  thinning: 0.55,
  smoothing: 0.5,
  streamline: 0.45,
} as const;

function strokeToPath(stroke: Stroke, isLast: boolean): Path2D | null {
  const outline = getStroke(stroke.points, {
    ...STROKE_OPTIONS,
    simulatePressure: stroke.simulatePressure,
    last: isLast,
  });
  if (!outline.length) return null;
  const path = new Path2D();
  path.moveTo(outline[0][0], outline[0][1]);
  // M-3: smooth outline using quadratic curves through midpoints instead of lineTo
  for (let i = 1; i < outline.length - 1; i++) {
    const mx = (outline[i][0] + outline[i + 1][0]) / 2;
    const my = (outline[i][1] + outline[i + 1][1]) / 2;
    path.quadraticCurveTo(outline[i][0], outline[i][1], mx, my);
  }
  if (outline.length > 1) {
    const lastPt = outline[outline.length - 1];
    path.lineTo(lastPt[0], lastPt[1]);
  }
  path.closePath();
  return path;
}

export const SignaturePadModal: React.FC<SignaturePadModalProps> = ({
  isOpen,
  onClose,
  onSave,
  title = 'Draw your signature',
  trapFocus = true,
  hidden = false,
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);
  const [strokes, setStrokes] = useState<Stroke[]>([]);
  const [saveError, setSaveError] = useState<string | null>(null);

  useDialogA11y(isOpen && trapFocus && !hidden, onClose, dialogRef);
  const [inkColor, setInkColor] = useState(SIGNATURE_INK_COLORS[0].value);

  // Mutable mirrors for rAF rendering during a gesture.
  const strokesRef = useRef<Stroke[]>(strokes);
  const currentStrokeRef = useRef<Stroke | null>(null);
  const inkColorRef = useRef(inkColor);
  const scaleRef = useRef(2);
  const rafRef = useRef<number | null>(null);
  const activePointerRef = useRef<number | null>(null);
  const padSessionOpenRef = useRef(false);

  const renderCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = canvas?.getContext('2d');
    if (!canvas || !ctx) return;
    const scale = scaleRef.current;
    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    // Strokes only — the guideline lives in CSS so it can never reach the export.
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = inkColorRef.current;
    for (const stroke of strokesRef.current) {
      const path = strokeToPath(stroke, true);
      if (path) ctx.fill(path);
    }
    const current = currentStrokeRef.current;
    if (current) {
      const path = strokeToPath(current, false);
      if (path) ctx.fill(path);
    }
  }, []);

  const scheduleRender = useCallback(() => {
    if (rafRef.current !== null) return;
    rafRef.current = requestAnimationFrame(() => {
      rafRef.current = null;
      renderCanvas();
    });
  }, [renderCanvas]);

  // Backing store = max(devicePixelRatio, 2) * CSS size for crisp HiDPI strokes.
  const setupCanvas = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const scale = Math.max(window.devicePixelRatio || 1, 2);
    scaleRef.current = scale;
    canvas.width = Math.max(1, Math.round(rect.width * scale));
    canvas.height = Math.max(1, Math.round(rect.height * scale));
    renderCanvas();
  }, [renderCanvas]);

  useEffect(() => {
    strokesRef.current = strokes;
    renderCanvas();
  }, [strokes, renderCanvas]);

  useEffect(() => {
    inkColorRef.current = inkColor;
    renderCanvas();
  }, [inkColor, renderCanvas]);

  useEffect(() => {
    if (!isOpen) {
      padSessionOpenRef.current = false;
      return;
    }
    if (hidden) return;
    if (!padSessionOpenRef.current) {
      padSessionOpenRef.current = true;
      setStrokes([]);
      strokesRef.current = [];
      setSaveError(null);
      currentStrokeRef.current = null;
      activePointerRef.current = null;
    }
    setupCanvas();
    const onResize = () => setupCanvas();
    window.addEventListener('resize', onResize);
    return () => {
      window.removeEventListener('resize', onResize);
      if (rafRef.current !== null) {
        cancelAnimationFrame(rafRef.current);
        rafRef.current = null;
      }
    };
  }, [isOpen, hidden, setupCanvas]);

  const undoStroke = useCallback(() => {
    setStrokes((prev) => prev.slice(0, -1));
  }, []);

  // Ctrl/Cmd+Z undoes the last stroke while the modal is open. Capture phase so
  // the workspace-level undo shortcut never fires behind the modal.
  useEffect(() => {
    if (!isOpen || hidden) return;
    const onKeyDown = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && !e.shiftKey && !e.altKey && e.key.toLowerCase() === 'z') {
        e.preventDefault();
        e.stopPropagation();
        undoStroke();
      }
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [isOpen, hidden, undoStroke]);

  const getPoint = (e: { clientX: number; clientY: number; pressure?: number }): number[] => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return [e.clientX - rect.left, e.clientY - rect.top, e.pressure || 0.5];
  };

  const handlePointerDown = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== null) return;
    e.preventDefault();
    // Capture so the stroke survives the pointer leaving the canvas.
    e.currentTarget.setPointerCapture(e.pointerId);
    activePointerRef.current = e.pointerId;
    currentStrokeRef.current = {
      points: [getPoint(e)],
      simulatePressure: e.pointerType !== 'pen',
    };
    scheduleRender();
  };

  const handlePointerMove = (e: React.PointerEvent<HTMLCanvasElement>) => {
    const stroke = currentStrokeRef.current;
    if (activePointerRef.current !== e.pointerId || !stroke) return;
    const native = e.nativeEvent as PointerEvent & { getCoalescedEvents?: () => PointerEvent[] };
    const events =
      typeof native.getCoalescedEvents === 'function' ? native.getCoalescedEvents() : [];
    const source = events.length ? events : [native];
    for (const ev of source) {
      stroke.points.push(getPoint(ev));
    }
    scheduleRender();
  };

  const finishStroke = (e: React.PointerEvent<HTMLCanvasElement>) => {
    if (activePointerRef.current !== e.pointerId) return;
    activePointerRef.current = null;
    const stroke = currentStrokeRef.current;
    currentStrokeRef.current = null;
    if (stroke && stroke.points.length) {
      // H-1: skip accidental taps — require at least a few px of travel
      const pts = stroke.points;
      if (pts.length > 1) {
        let minX = pts[0][0], maxX = pts[0][0];
        let minY = pts[0][1], maxY = pts[0][1];
        for (let i = 1; i < pts.length; i++) {
          if (pts[i][0] < minX) minX = pts[i][0];
          if (pts[i][0] > maxX) maxX = pts[i][0];
          if (pts[i][1] < minY) minY = pts[i][1];
          if (pts[i][1] > maxY) maxY = pts[i][1];
        }
        const bboxW = maxX - minX;
        const bboxH = maxY - minY;
        if (bboxW < 3 && bboxH < 3) return; // invisible tap — discard
      }
      setSaveError(null);
      setStrokes((prev) => [...prev, stroke]);
    }
  };

  const clearCanvas = () => {
    currentStrokeRef.current = null;
    strokesRef.current = [];
    setStrokes([]);
    setSaveError(null);
    renderCanvas();
  };

  const handleSave = () => {
    if (!strokesRef.current.length) {
      setSaveError('Please draw your signature first.');
      return;
    }
    const sourceCanvas = canvasRef.current;
    if (!sourceCanvas) return;
    const scale = scaleRef.current;
    // Export strokes only onto a fresh transparent canvas at full backing
    // resolution, then crop to the ink bounding box plus a small margin.
    // This modal only CREATES the image; the parent (SignatureCreationFlow) decides
    // what happens next (Use once vs Save & place) and controls closing. We intentionally
    // do NOT call onClose() here, otherwise the parent's choice dialog could never appear.
    const exportCanvas = document.createElement('canvas');
    exportCanvas.width = sourceCanvas.width;
    exportCanvas.height = sourceCanvas.height;
    // M-4: willReadFrequently since trimCanvasToInk reads pixels immediately after draw
    const ctx = exportCanvas.getContext('2d', { willReadFrequently: true });
    if (!ctx) return;
    ctx.setTransform(scale, 0, 0, scale, 0, 0);
    ctx.fillStyle = inkColorRef.current;
    for (const stroke of strokesRef.current) {
      const path = strokeToPath(stroke, true);
      if (path) ctx.fill(path);
    }
    // H-1: ensure the drawn ink has a meaningful bounding box before saving
    const bbox = getInkBoundingBox(exportCanvas);
    if (!bbox) {
      setSaveError('Please draw your signature first.');
      return;
    }
    // F28: distinguish "drew nothing" from "drew something too small". Reject
    // dots/taps (both dimensions tiny), but allow thin horizontal/vertical strokes.
    if (Math.max(bbox.width, bbox.height) < 10) {
      setSaveError('Signature is too small — draw a larger, more visible signature.');
      return;
    }
    setSaveError(null);
    const trimmed = trimCanvasToInk(exportCanvas, Math.round(EXPORT_TRIM_MARGIN_CSS_PX * scale));
    onSave(trimmed.toDataURL('image/png'));
  };

  if (!isOpen) return null;

  const hasDrawn = strokes.length > 0;

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center bg-black/60 p-4${hidden ? ' invisible pointer-events-none' : ''}`}
      role="presentation"
      aria-hidden={hidden}
      onClick={onClose}
    >
      <div
        ref={dialogRef}
        role="dialog"
        aria-modal="true"
        aria-labelledby="signature-pad-title"
        tabIndex={-1}
        className="modal rounded-2xl shadow-2xl w-full max-w-[620px] overflow-hidden bg-white dark:bg-slate-900 outline-none"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="px-6 py-4 border-b flex items-center justify-between">
          <div>
            <div id="signature-pad-title" className="font-semibold text-lg dark:text-slate-100">{title}</div>
            <div className="text-xs text-slate-500">Draw inside the box. Your signature will be embedded visually.</div>
          </div>
          <button type="button" onClick={onClose} aria-label="Close" className="text-slate-400 hover:text-slate-600 dark:hover:text-slate-200 text-2xl leading-none">×</button>
        </div>

        <div className="p-6 bg-slate-50">
          <canvas
            ref={canvasRef}
            role="img"
            aria-label="Signature drawing area. Drawing requires a pointer or touch. For a keyboard-accessible signature, use the Type option."
            className="signature-canvas w-full touch-none bg-white"
            style={{
              height: PAD_HEIGHT_PX,
              backgroundImage: `linear-gradient(to top, transparent ${GUIDELINE_OFFSET_PX - 1}px, #e2e8f0 ${GUIDELINE_OFFSET_PX - 1}px, #e2e8f0 ${GUIDELINE_OFFSET_PX}px, transparent ${GUIDELINE_OFFSET_PX}px)`,
            }}
            onPointerDown={handlePointerDown}
            onPointerMove={handlePointerMove}
            onPointerUp={finishStroke}
            onPointerCancel={finishStroke}
          />
          <div className="flex items-center justify-between mt-3 text-xs text-slate-500">
            <div>Use mouse, trackpad, touch, or pen</div>
            <div className="flex items-center gap-3">
              <div className="flex items-center gap-1.5" role="radiogroup" aria-label="Ink color">
                {SIGNATURE_INK_COLORS.map((color) => (
                  <button
                    key={color.id}
                    type="button"
                    role="radio"
                    aria-checked={inkColor === color.value}
                    aria-label={color.label}
                    title={color.label}
                    onClick={() => setInkColor(color.value)}
                    className="h-5 w-5 rounded-full border-2 transition-transform hover:scale-110"
                    style={{
                      backgroundColor: color.value,
                      borderColor: inkColor === color.value ? 'var(--accent)' : '#e2e8f0',
                    }}
                  />
                ))}
              </div>
              <button
                onClick={undoStroke}
                disabled={!hasDrawn}
                title="Undo last stroke (Ctrl+Z)"
                aria-label="Undo last stroke"
                className="text-accent hover:underline disabled:text-slate-300 disabled:no-underline"
              >
                Undo
              </button>
              <button
                onClick={clearCanvas}
                disabled={!hasDrawn}
                aria-label="Clear all strokes"
                className="text-accent hover:underline disabled:text-slate-300 disabled:no-underline"
              >
                Clear
              </button>
            </div>
          </div>
        </div>

        <div className="px-6 py-4 bg-white dark:bg-slate-900 flex flex-wrap items-center justify-end gap-3 border-t dark:border-slate-700">
          {/* A12: visible, focusable hint so keyboard/AT users know a Type alternative exists */}
          <p className="flex-1 min-w-[12rem] self-center text-xs text-slate-400 dark:text-slate-500">
            Keyboard users: a <strong>Type</strong> option is available for an accessible signature.
          </p>
          {/* Reserve space so validation errors don't shift action buttons (e2e stability). */}
          <div className="min-h-[1.25rem] flex-1 min-w-[10rem] flex items-center">
            {saveError && (
              <span className="text-sm text-state-danger" role="alert">{saveError}</span>
            )}
          </div>
          <button
            type="button"
            onClick={onClose}
            className="px-5 py-2 rounded-lg border border-slate-200 text-sm font-medium hover:bg-slate-50"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleSave}
            className="px-6 py-2 rounded-lg bg-slate-900 text-white text-sm font-semibold"
          >
            Use Signature
          </button>
        </div>
      </div>
    </div>
  );
};
